// src/pages/ContactPage.js
import React, { useState } from 'react';
import { Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, Alert, Snackbar, CircularProgress } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import { motion } from 'framer-motion';
import useSEO from '../hooks/useSEO';
import Breadcrumbs from '../components/common/Breadcrumbs';
import apiService from '../services/apiService';

const BREADCRUMBS = [{ name: 'Contact', url: 'https://dublabs.ai/contact' }];

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.25, 0.1, 0.25, 1] },
  }),
};

const FIELD_SX = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#0A0A0A',
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.08)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
  },
  '& .MuiInputLabel-root': { color: '#555', fontFamily: 'Inter, sans-serif', '&.Mui-focused': { color: '#fff' } },
  '& .MuiInputBase-input': { color: '#fff', fontFamily: 'Inter, sans-serif', fontSize: '14px' },
};

const CONTACT_INFO = [
  { Icon: EmailIcon, label: 'Email', value: 'hello@aiguru.in', href: 'mailto:hello@aiguru.in' },
  { Icon: PhoneIcon, label: 'Phone', value: '+91 74057 57329', href: 'tel:+917405757329' },
  { Icon: LocationOnIcon, label: 'Office', value: 'Gurukul Road, Ahmedabad, Gujarat – 380052' },
];

const INQUIRY_TYPES = ['Get in Touch', 'General Inquiry', 'Pricing / Quote', 'Partnership', 'Support', 'Other'];

const EMPTY_FORM = { name: '', email: '', phone: '', company: '', subject: '', message: '', inquiry_type: '' };

export default function ContactPage() {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useSEO({
    title: 'Contact DubLabs — AI Video Dubbing in 32 Languages',
    description: 'Contact DubLabs about AI video dubbing in 32 languages. We\'ll respond within one business day with a quote, timeline, and next steps for your project.',
    canonical: 'https://dublabs.ai/contact',
    breadcrumbs: BREADCRUMBS,
    dateModified: '2026-07-30',
  });

  const handleChange = (e) => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiService.submitContactForm(formData);
      setSnackbar({ open: true, message: "Thank you! We'll get back to you within 24 hours.", severity: 'success' });
      setFormData(EMPTY_FORM);
    } catch {
      setSnackbar({ open: true, message: 'Failed to send. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ backgroundColor: '#000000', minHeight: '100vh', py: { xs: 8, md: 14 }, px: { xs: 2, md: 4 } }}>
      <Breadcrumbs items={BREADCRUMBS} />
      <Box sx={{ maxWidth: '1000px', mx: 'auto' }}>

        {/* Header */}
        <Box
          component={motion.div}
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          variants={fadeUp}
          sx={{ textAlign: 'center', mb: { xs: 8, md: 12 } }}
        >
          <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: 'Inter, sans-serif', mb: 2 }}>
            Contact
          </Typography>
          <Typography variant="h1" sx={{ fontSize: { xs: '2.4rem', md: '3.4rem' }, fontWeight: 800, letterSpacing: '-0.035em', color: '#FFFFFF', fontFamily: 'Inter, sans-serif', mb: 2 }}>
            Get in touch.
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '1.1rem', fontFamily: 'Inter, sans-serif', maxWidth: '520px', mx: 'auto', lineHeight: 1.65 }}>
            Tell us about your project — we'll respond within one business day with a quote and next steps.
          </Typography>
        </Box>

        {/* Content grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '300px 1fr' }, gap: 3, alignItems: 'start' }}>

          {/* Left: Contact info */}
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={1}
          >
            {/* Info cards */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'row', lg: 'column' }, gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
              {CONTACT_INFO.map(({ Icon, label, value, href }) => (
                <Box key={label} sx={{
                  flex: 1, minWidth: { xs: '140px', lg: 'unset' },
                  p: 2.5, borderRadius: '10px',
                  backgroundColor: '#0A0A0A',
                  border: '1px solid rgba(255,255,255,0.06)',
                  transition: 'border-color 0.2s ease',
                  '&:hover': { borderColor: href ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.06)' },
                }}>
                  <Icon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.35)', mb: 1 }} />
                  <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '13px', fontFamily: 'Inter, sans-serif', mb: 0.25 }}>{label}</Typography>
                  <Typography
                    component={href ? 'a' : 'span'}
                    href={href}
                    sx={{
                      color: 'rgba(255,255,255,0.45)',
                      fontSize: '13px', fontFamily: 'Inter, sans-serif',
                      textDecoration: 'none', display: 'block',
                      '&:hover': { color: href ? '#FFFFFF' : 'rgba(255,255,255,0.45)' },
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Response note */}
            <Box sx={{ p: 2.5, borderRadius: '10px', backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.06)' }}>
              <Typography sx={{ color: '#FFFFFF', fontWeight: 600, fontSize: '13px', fontFamily: 'Inter, sans-serif', mb: 0.5 }}>Response time</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '13px', fontFamily: 'Inter, sans-serif', lineHeight: 1.65 }}>
                We respond within 24 hours on business days. For urgent matters, include "URGENT" in the subject line.
              </Typography>
            </Box>
          </Box>

          {/* Right: Form */}
          <Box
            component={motion.div}
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            variants={fadeUp} custom={2}
            sx={{ p: { xs: 3, md: 4 }, borderRadius: '16px', backgroundColor: '#0A0A0A', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '18px', fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em', mb: 4 }}>
              Send us a message
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleChange} required size="small" sx={FIELD_SX} />
                <TextField fullWidth label="Email" name="email" type="email" value={formData.email} onChange={handleChange} required size="small" sx={FIELD_SX} />
                <TextField fullWidth label="Phone (optional)" name="phone" value={formData.phone} onChange={handleChange} size="small" sx={FIELD_SX} />
                <TextField fullWidth label="Company" name="company" value={formData.company} onChange={handleChange} size="small" sx={FIELD_SX} />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <FormControl fullWidth size="small" required sx={FIELD_SX}>
                  <InputLabel>Inquiry Type</InputLabel>
                  <Select name="inquiry_type" value={formData.inquiry_type} onChange={handleChange} label="Inquiry Type"
                    MenuProps={{ PaperProps: { sx: { backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px' } } }}>
                    {INQUIRY_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#fff' }}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField fullWidth label="Subject" name="subject" value={formData.subject} onChange={handleChange} required size="small" sx={FIELD_SX} />
              </Box>

              <TextField
                fullWidth multiline rows={5}
                label="Message" name="message"
                value={formData.message} onChange={handleChange}
                required placeholder="Tell us about your project or question..."
                sx={{ ...FIELD_SX, mb: 3 }}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <Button
                  type="submit"
                  disabled={loading}
                  component={motion.button}
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  startIcon={loading ? <CircularProgress size={16} sx={{ color: '#000' }} /> : <SendIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    backgroundColor: '#FFFFFF', color: '#000000',
                    fontWeight: 700, fontSize: '14px', px: 3.5, py: 1.25,
                    borderRadius: '8px', textTransform: 'none',
                    fontFamily: 'Inter, sans-serif',
                    '&:hover': { backgroundColor: '#F0F0F0' },
                    '&:disabled': { backgroundColor: 'rgba(255,255,255,0.2)', color: 'rgba(0,0,0,0.4)' },
                  }}
                >
                  {loading ? 'Sending…' : 'Send Message'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          sx={{ borderRadius: '8px', fontFamily: 'Inter, sans-serif' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
